import { Node } from '@tiptap/core';

export type AssetImageAlign = 'left' | 'center' | 'wide';

export interface AssetImageAttrs {
  src: string;
  alt?: string | null;
  asid?: number | null;
  align?: AssetImageAlign;
  caption?: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    assetImage: {
      setAssetImage: (attrs: AssetImageAttrs) => ReturnType;
      updateAssetImage: (attrs: Partial<AssetImageAttrs>) => ReturnType;
    };
  }
}

export const AssetImage = Node.create({
  name: 'assetImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      asid: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute('data-asid') ?? el.querySelector?.('img')?.getAttribute('data-asid');
          return v != null && v !== '' ? Number(v) : null;
        },
        renderHTML: () => ({}),
      },
      align: {
        default: 'center' as AssetImageAlign,
        parseHTML: (el) => {
          const cls = el.getAttribute('class') || '';
          if (cls.includes('cms-align-left')) return 'left';
          if (cls.includes('cms-align-wide')) return 'wide';
          return 'center';
        },
        renderHTML: () => ({}),
      },
      caption: {
        default: null,
        parseHTML: (el) => el.querySelector?.('figcaption')?.textContent ?? null,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure.cms-figure',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          const img = el.querySelector('img');
          if (!img?.getAttribute('src')) return false;
          const asidRaw = img.getAttribute('data-asid');
          const cls = el.getAttribute('class') || '';
          let align: AssetImageAlign = 'center';
          if (cls.includes('cms-align-left')) align = 'left';
          else if (cls.includes('cms-align-wide')) align = 'wide';
          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt'),
            asid: asidRaw != null && asidRaw !== '' ? Number(asidRaw) : null,
            align,
            caption: el.querySelector('figcaption')?.textContent ?? null,
          };
        },
      },
      {
        tag: 'img[src]',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (el.closest('figure.cms-figure')) return false;
          const asidRaw = el.getAttribute('data-asid');
          return {
            src: el.getAttribute('src'),
            alt: el.getAttribute('alt'),
            asid: asidRaw != null && asidRaw !== '' ? Number(asidRaw) : null,
            align: 'center' as AssetImageAlign,
            caption: null,
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { src, alt, asid, align, caption } = node.attrs as AssetImageAttrs;
    const alignClass = `cms-align-${align || 'center'}`;
    const imgAttrs: Record<string, string> = {
      src: src || '',
      class: 'cms-image',
      loading: 'lazy',
    };
    if (alt) imgAttrs.alt = alt;
    if (asid != null) imgAttrs['data-asid'] = String(asid);

    const children: any[] = [['img', imgAttrs]];
    if (caption) children.push(['figcaption', {}, caption]);

    return ['figure', { class: `cms-figure ${alignClass}` }, ...children];
  },

  addCommands() {
    return {
      setAssetImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              align: 'center',
              ...attrs,
            },
          }),
      updateAssetImage:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, attrs),
    };
  },
});

export default AssetImage;
